import { BaseMepFilterViewModel, MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { MepInsightsRequest } from "../../../../../../../../models/mep/mep-insights.models";
import { Claims } from "../../../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../../../services/authentication/user-claims.service";
import { MepLookupFiltersHandler } from "../../../../common-vm/mep-lookup-filters-handler";
import { computedFrom } from "aurelia-binding";
import { transient } from "aurelia-dependency-injection"; 
import { Reflection } from "../../../../../../../../utilities/reflection";
import { useView } from "aurelia-framework";

const mobileFiltersWidthDelimiter: number = 1024; //Screen width above which mobile filters are hidden

@transient()
@useView('views/pages/modules/mep/tabs/mep-insights/partials/mep-insights-filter/mep-insights-filter.html')
export class MepInsightsFilter extends BaseMepFilterViewModel {

    public isMobileFilterOpen: boolean;

    public supportsCargoType: boolean;

    private lastRequest: MepInsightsRequest;

    public canShowGhgRatingFilter : boolean;
 
    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.emissionTypes')
    get emissionTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.emissionTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.pointsOfInterests')
    get pointsOfInterests(): MepLookupItem[] {
        return this.lookupFiltersHandler.pointsOfInterests;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.operationModes')
    get operationModes(): MepLookupItem[] {
        return this.lookupFiltersHandler.operationModes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.scopes')
    get scopes(): MepLookupItem[] {
        return this.lookupFiltersHandler.scopes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.vesselTypeGroups')
    get vesselTypeGroups(): MepLookupItem[] {
        return this.lookupFiltersHandler.vesselTypeGroups;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.weightBuckets')
    get weightBuckets(): MepLookupItem[] {
        return this.lookupFiltersHandler.weightBuckets;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.isLookupDataRefreshing')
    get isLookupDataRefreshing(): boolean {
        return this.lookupFiltersHandler.isLookupDataRefreshing;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.cargoTypes')
    get cargoTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.cargoTypes;
    }

    refreshDataFunc: (request: MepInsightsRequest) => Promise<string[]>;

    selectedEmissionType: MepLookupItem = null;

    applyFiltersFunc: () => void;

    constructor(portId: number, supportsCargoType: boolean, refreshDataFunc: (request: MepInsightsRequest) => Promise<string[]>,
        private notificationService: NotificationClientService, mepLookupService: MepLookupService,
        private userClaimsService: UserClaimsService) {
        super();
        this.portId = portId;
        this.supportsCargoType = supportsCargoType;
        this.refreshDataFunc = refreshDataFunc;
        this.applyFiltersFunc = () => this.applyFilters();
        this.lookupFiltersHandler = new MepLookupFiltersHandler(portId, this.applyFiltersFunc, mepLookupService);
        this.lookupFiltersHandler.includeWeightBucketsLookup = true;
        this.canShowGhgRatingFilter = this.userClaimsService.hasPermission(Claims.ShowGHG);
    }

    attached() {
        window.addEventListener('resize', this.adjustFilters);
    }

    detached() {
        window.removeEventListener('resize', this.adjustFilters);
    }

    adjustFilters = () => {
        if (window.innerWidth >= mobileFiltersWidthDelimiter) {
            this.isMobileFilterOpen = false;
        }
        else {
            this.closeAllFilters();
        }
    }

    initLookups() {
        return this.lookupFiltersHandler.initLookups();
    }

    onSelectedItemChanged(newValue: { id: number, name: string }, oldValue: { id: number, name: string }) {
        if (!oldValue || (newValue && newValue.id != oldValue.id)) {
            this.applyFilters();
        }
    }

    onSelectedMonthsChanged() {
        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        const refreshLookupsPromise = this.lookupFiltersHandler.onSelectedMonthsChanged();
        if (refreshLookupsPromise) {
            this.clearAllFilters();
            refreshLookupsPromise.then(() => this.applyFilters())
        }
    }

    setFiltersFromParams(params: any) {
        if (params.year)
            this.lookupFiltersHandler.selectedYear = params.year;

        if (params.emissionTypeId)
            this.selectedEmissionType = this.emissionTypes.filter(x => x.id == Number(params.emissionTypeId))[0];

        if (params.pointOfInterestIds)
            this.selectedPointsOfInterest = params.pointOfInterestIds.map(x => Number(x));

        if (params.vesselTypeGroupIds)
            this.selectedVesselTypeGroups = params.vesselTypeGroupIds.map(x => Number(x));

        if (params.operationModeIds)
            this.selectedOperationModes = params.operationModeIds.map(x => Number(x));

        if (params.scopeIds)
            this.selectedScopes = params.scopeIds.map(x => Number(x));

        if (params.cargoTypeIds)
            this.selectedCargoTypes = params.cargoTypeIds.map(x => Number(x));

        if (params.portId) {
            this.portId = params.portId;
            this.supportsCargoType = params.supportsCargoType;
        }

        this.applyFilters();
    }

    isRefreshingData: boolean = false;

    applyFilters(applySelectedFilters: boolean = false) {
        if (applySelectedFilters) {
            //Apply age filter
            this.applyAgeFilter({ low: this.ageFirstFilter, high: this.ageSecondFilter }, false);
        }

        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        if (this.refreshDataFunc) {
            this.isRefreshingData = true;

            if (this.checkEqualityOfRequests(this.lastRequest, this.getRequest())) {
                this.isRefreshingData = false;
                return; // no need to refresh everything is the same 
            }

            this.lastRequest = this.getRequest();

            this.refreshDataFunc(this.getRequest()).then(() => {
                this.isRefreshingData = false;
                this.lookupFiltersHandler.skipRefreshOfLookupsBecauseOfInit = false;
            }).catch(e => {
                this.isRefreshingData = false;
                if (typeof e === 'string') {
                    this.notificationService.show("MEP Error", e, NotificationType.Error);
                }
                else {
                    this.notificationService.show("MEP Error", "Error refreshing data", NotificationType.Error);
                }
            });
        }
    }

    public getRequest(): MepInsightsRequest {
        return {
            portId: this.portId,
            pointOfInterestIds: this.selectedPointsOfInterest,
            vesselTypeGroupIds: this.selectedVesselTypeGroups,
            operationModeIds: this.selectedOperationModes,
            scopeIds: this.selectedScopes,
            cargoTypeIds: this.supportsCargoType ? this.selectedCargoTypes : null,
            emissionTypeId: this.selectedEmissionType ? this.selectedEmissionType.id : 0,
            ghgRatings: this.selectedGhgRatingWeights,
            weightBucketIds: this.selectedDwtBucketIds.join(),
            ageFrom: this.ageFirstFilter ? this.ageFirstFilter : this.ageDefaults.low,
            ageTo: this.ageSecondFilter ? this.ageSecondFilter : this.ageDefaults.high,
            year: this.selectedYear ? this.selectedYear : null,
            months: this.selectedMonths ? this.selectedMonths.map(x => x.id) : null,
        };
    }

    public checkEqualityOfRequests(lastRequest, newRequest) {
        if (!lastRequest || !newRequest) return false

        const keysLastRequest = Object.keys(lastRequest);
        const keysNewRequest = Object.keys(newRequest);

        if (keysLastRequest.length !== keysNewRequest.length) {
            return false;
        }

        for (const key of keysLastRequest) {
            if (Array.isArray(lastRequest[key]) && Array.isArray(newRequest[key])) {
                if (lastRequest[key].length !== newRequest[key].length) {
                    return false;
                } else {
                    for (let i = 0; i < lastRequest[key].length; ++i) {
                        if (lastRequest[key][i] !== newRequest[key][i]) {
                            return false;
                        }
                    }
                }
            } else if (lastRequest[key] !== newRequest[key]) {
                return false;
            }
        }
        return true;
    }

    //#region PointsOfInterest Filter

    selectedPointsOfInterest: number[] = [];
    selectedPointsOfInterestForPrint: MepLookupItem[] = [];

    public showPoiFilter: boolean = false;

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.poiFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.vesselTypeFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.operationModeFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.cargoTypeFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.ghgRatingFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.dwtFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.ageFilterActive),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.scopeFilterActive))
    get filtersActive(): boolean {
        return this.poiFilterActive || this.vesselTypeFilterActive || this.operationModeFilterActive || 
        this.scopeFilterActive || this.cargoTypeFilterActive || this.ghgRatingFilterActive || this.dwtFilterActive || this.ageFilterActive;
    }

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedPointsOfInterest),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedPointsOfInterest.length))
    get poiFilterActive(): boolean {
        return this.selectedPointsOfInterest.length > 0;
    }

    togglePoiFilters() {
        if (!this.showPoiFilter) {
            this.closeAllFilters();
        }

        this.showPoiFilter = !this.showPoiFilter;
    }

    applyPoiFilter($event: any) {
        this.selectedPointsOfInterest = MepLookupItem.getIdsFromLookups(this.pointsOfInterests, $event.filters);
        this.selectedPointsOfInterestForPrint = $event.filters;
        this.lookupFiltersHandler.pointsOfInterests = $event.items

        this.applyFilters();
        this.togglePoiFilters();
    }

    clearPoiFilter(shouldRefreshData: boolean = true) {
        this.selectedPointsOfInterest = [];
        this.selectedPointsOfInterestForPrint = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region VesselTypeGroups Filter
    selectedVesselTypeGroups: number[] = [];
    selectedVesselTypeGroupsForPrint: MepLookupItem[] = [];
    public showVesselTypeFilter: boolean = false;

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedVesselTypeGroups),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedVesselTypeGroups.length))
    get vesselTypeFilterActive(): boolean {
        return this.selectedVesselTypeGroups.length > 0;
    }

    toggleVesselTypeFilters() {
        if (!this.showVesselTypeFilter) {
            this.closeAllFilters();
        }

        this.showVesselTypeFilter = !this.showVesselTypeFilter;
    }

    applyVesselTypeFilter($event: any) {
        this.selectedVesselTypeGroups = MepLookupItem.getIdsFromLookups(this.vesselTypeGroups, $event.filters);
        this.selectedVesselTypeGroupsForPrint = $event.filters;
        this.lookupFiltersHandler.vesselTypeGroups = $event.items

        this.applyFilters();
        this.toggleVesselTypeFilters();
    }

    clearVesselTypeFilter(shouldRefreshData: boolean = true) {
        this.selectedVesselTypeGroups = [];
        this.selectedVesselTypeGroupsForPrint = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region OperationModes Filter
    selectedOperationModes: number[] = [];
    selectedOperationModesForPrint: MepLookupItem[] = [];
    public showOperationModeFilter: boolean = false;

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedOperationModes),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedOperationModes.length))
    get operationModeFilterActive(): boolean {
        return this.selectedOperationModes.length > 0;
    }

    toggleOperationModeFilters() {
        if (!this.showOperationModeFilter) {
            this.closeAllFilters();
        }

        this.showOperationModeFilter = !this.showOperationModeFilter;
    }

    applyOperationModeFilter($event: any) {
        this.selectedOperationModes = MepLookupItem.getIdsFromLookups(this.operationModes, $event.filters);
        this.selectedOperationModesForPrint = $event.filters;
        this.lookupFiltersHandler.operationModes = $event.items

        this.applyFilters();
        this.toggleOperationModeFilters();
    }

    clearOperationModeFilter(shouldRefreshData: boolean = true) {
        this.selectedOperationModes = [];
        this.selectedOperationModesForPrint = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region Scope filter
    selectedScopes: number[] = [];
    selectedScopesForPrint: MepLookupItem[] = [];
    public showScopeFilter: boolean = false;

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedScopes),
        Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedScopes.length))
    get scopeFilterActive(): boolean {
        return this.selectedScopes.length > 0;
    }

    toggleScopeFilters() {
        if (!this.showScopeFilter) {
            this.closeAllFilters();
        }

        this.showScopeFilter = !this.showScopeFilter;
    }

    applyScopeFilter($event: any) {
        this.selectedScopes = MepLookupItem.getIdsFromLookups(this.scopes, $event.filters);
        this.selectedScopesForPrint = $event.filters;
        this.lookupFiltersHandler.scopes = $event.items

        this.applyFilters();
        this.toggleScopeFilters();
    }

    clearScopeFilter(shouldRefreshData: boolean = true) {
        this.selectedScopes = [];
        this.selectedScopesForPrint = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region CargoTypes Filter
    selectedCargoTypes: number[] = [];
    selectedCargoTypesForPrint: MepLookupItem[] = [];
    public showCargoTypeFilter: boolean = false;

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedCargoTypes),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedCargoTypes.length))
    get cargoTypeFilterActive(): boolean {
        return this.selectedCargoTypes.length > 0;
    }

    toggleCargoTypeFilters() {
        if (!this.showCargoTypeFilter) {
            this.closeAllFilters();
        }

        this.showCargoTypeFilter = !this.showCargoTypeFilter;
    }

    applyCargoTypeFilter($event: any) {
        this.selectedCargoTypes = MepLookupItem.getIdsFromLookups(this.cargoTypes, $event.filters);
        this.selectedCargoTypesForPrint = $event.filters;
        this.lookupFiltersHandler.cargoTypes = $event.items

        this.applyFilters();
        this.toggleCargoTypeFilters();
    }

    clearCargoTypeFilter(shouldRefreshData: boolean = true) {
        this.selectedCargoTypes = [];
        this.selectedCargoTypesForPrint = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

        
    //#region GhgRating Filter
    public showGhgRatingFilter: boolean = false;
    public selectedGhgRatingWeights: number[] = [];
    public selectedGhgRatingItems: number[] = [];

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedGhgRatingWeights),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedGhgRatingWeights.length))
    get ghgRatingFilterActive(): boolean {
        return this.selectedGhgRatingWeights.length > 0;
    }

    toggleGhgRatingFilters() {
        if (!this.showGhgRatingFilter) {
            this.closeAllFilters();
        }

        this.showGhgRatingFilter = !this.showGhgRatingFilter;
    }

    applyGhgRating(items: number[]) {
        this.selectedGhgRatingWeights = items;

        this.applyFilters();
        this.toggleGhgRatingFilters();
    }

    clearGhgRatingFilter(shouldRefreshData: boolean = true) {
        this.selectedGhgRatingWeights = [];
        this.selectedGhgRatingItems = [];
        
        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region DWT Filter
    public showDwtFilter: boolean = false;
    public selectedDwtBucketIds: number[] = [];
    public selectedDwtBuckets: MepLookupItem[] = [];

    toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }

        this.showDwtFilter = !this.showDwtFilter;
    }

    applyDwtFilter($event: any) {
        this.selectedDwtBucketIds = MepLookupItem.getIdsFromLookups(this.weightBuckets, $event.filters);
        this.selectedDwtBuckets = $event.filters;
        this.lookupFiltersHandler.weightBuckets = $event.items

        this.applyFilters();
        this.toggleDwtFilters();
    }
 
    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedDwtBucketIds),
    Reflection.getPropertyPath<MepInsightsFilter>(z => z.selectedDwtBucketIds.length))
    get dwtFilterActive(): boolean {
        return this.selectedDwtBucketIds.length > 0;
    }

    clearDwtFilter(shouldRefreshData: boolean = true) {
        this.selectedDwtBucketIds = [];
        this.selectedDwtBuckets = [];

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion

    //#region Age Filter
    public showAgeFilter: boolean = false;
    public ageFirstFilter: number;
    public ageSecondFilter: number;
    public mobileAgeFilter: { low: number, high: number };
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public ageDefaults: { low: number, high: number } = { low: 0, high: 100 };
    public ageIsApplied: boolean = false;

    toggleAgeFilters() {
        if (!this.showAgeFilter) {
            this.closeAllFilters();
        }

        this.showAgeFilter = !this.showAgeFilter;
    }

    /**
     * Apply the 'age' filter
     * @param filterValue min and max age values
     * @param reload Apply filters after setting min/max age values
     */
    applyAgeFilter(filterValue: { low: number, high: number } = null, reload: boolean = true) {
        if (filterValue) {
            this.ageFirstFilter = filterValue.low;
            this.ageSecondFilter = filterValue.high;
            // If the min/max ages are not equal to the min and max values
            if (!(this.ageFirstFilter == this.ageDefaults.low && this.ageSecondFilter == this.ageDefaults.high)) {
                this.ageIsApplied = true;
            }
        }

        if (reload) {
            this.applyFilters();
        }
        this.toggleAgeFilters();
    }

    @computedFrom(Reflection.getPropertyPath<MepInsightsFilter>(z => z.ageIsApplied))
    get ageFilterActive(): boolean {
        return this.ageIsApplied;
    }

    clearAgeFilter(shouldRefreshData: boolean = true) {
        this.ageFirstFilter = this.ageDefaults.low;
        this.ageSecondFilter = this.ageDefaults.high;
        this.mobileAgeFilter = { low: null, high: null };
        this.ageIsApplied = false;

        if (!shouldRefreshData)
            return;

        this.isRefreshingData = false;
        this.applyFilters();
    }
    //#endregion


    clearAllFilters(shouldRefreshData: boolean = true) {
        this.clearPoiFilter(shouldRefreshData);
        this.clearOperationModeFilter(shouldRefreshData);
        this.clearScopeFilter(shouldRefreshData);
        this.clearCargoTypeFilter(shouldRefreshData);
        this.clearVesselTypeFilter(shouldRefreshData);
        this.clearGhgRatingFilter(shouldRefreshData);
        this.clearDwtFilter(shouldRefreshData);
        this.clearAgeFilter(shouldRefreshData);

        if (!shouldRefreshData)
            this.applyFilters();
    }

    get mobileFilterSelected() {
        return !this.showPoiFilter || !this.showVesselTypeFilter || !this.showOperationModeFilter || !this.showCargoTypeFilter ||
            !this.showGhgRatingFilter || !this.showDwtFilter || !this.showAgeFilter || !this.showScopeFilter;
    }

    toggleMobileFilterDialog() {
        if (this.isMobileFilterOpen) {
            this.closeAllFilters();
        }

        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    applyMobileFilters() {
        this.isMobileFilterOpen = false;
        this.applyFilters();
    }

    get isOnlyTugBoatVesselTypeSelected() {
        return this.selectedVesselTypeGroups.length == 1 &&
            this.vesselTypeGroups.filter(x => x.id == this.selectedVesselTypeGroups[0])[0].name == 'Tugboat'
    }

    /**
     * Close all filters
     */
    closeAllFilters() {
        this.showAgeFilter = false;
        this.showDwtFilter = false;
        this.showGhgRatingFilter = false;
        this.showOperationModeFilter = false;
        this.showScopeFilter = false;
        this.showCargoTypeFilter = false;
        this.showPoiFilter = false;
        this.showVesselTypeFilter = false;
    }
}